import { z } from "zod";
import { createTransform } from "redux-persist";

export function validateOrDefault<T>(
    schema: z.ZodType<T>,
    fallback: T,
    data: unknown,
): T {
    const result = schema.safeParse(data);
    return result.success ? result.data : fallback;
}

export const sectionVisibilitySchema = z.object({
    overdue: z.boolean(),
    today: z.boolean(),
    tomorrow: z.boolean(),
    future: z.boolean(),
    none: z.boolean(),
});

export const designSchema = z.object({
    theme: z.enum(["light", "dark", "system"]),
    sidebarState: z.enum(["expanded", "collapsed"]),
});

export const referenceFilterSchema = z.object({
    search: z.string(),
    orgTypes: z.array(z.string()),
    communityTypes: z.array(z.string()),
    contactCondition: z.unknown().nullable(),
    businessCondition: z.unknown().nullable(),
    communityCondition: z.unknown().nullable(),
    assignedUserId: z.string(),
    geoParams: z.unknown().nullable(),
});

const defaultSectionVisibility = {
    overdue: true,
    today: true,
    tomorrow: true,
    future: true,
    none: true,
};

const defaultDesign = {
    theme: "light" as const,
    sidebarState: "expanded" as const,
};

export const taskDashboardValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown, key: string) => {
        if (key === "sectionVisibility") {
            return validateOrDefault(
                sectionVisibilitySchema,
                defaultSectionVisibility,
                outbound,
            );
        }
        return outbound;
    },
);

export const designValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown, key: string) => {
        if (key === "theme") {
            return validateOrDefault(
                designSchema.shape.theme,
                defaultDesign.theme,
                outbound,
            );
        }
        if (key === "sidebarState") {
            return validateOrDefault(
                designSchema.shape.sidebarState,
                defaultDesign.sidebarState,
                outbound,
            );
        }
        return outbound;
    },
);

export const calendarSchema = z.object({
    viewMode: z.enum(["month", "week", "day"]),
});

const defaultCalendar = {
    viewMode: "week" as const,
};

export const calendarValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown, key: string) => {
        if (key === "viewMode") {
            return validateOrDefault(
                calendarSchema.shape.viewMode,
                defaultCalendar.viewMode,
                outbound,
            );
        }
        return outbound;
    },
);

export const referenceDashboardValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown) => {
        if (!outbound || typeof outbound !== "object") {
            return outbound;
        }
        const state = outbound as Record<string, unknown>;
        if (!state.searchFilter) {
            return outbound;
        }
        const result = referenceFilterSchema.safeParse(state.searchFilter);
        if (result.success) {
            return { ...state, searchFilter: result.data };
        }
        return { ...state, searchFilter: undefined };
    },
    { whitelist: ["referenceDashboard"] },
);
